import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Zap, Plus, Eye, Loader2, ArrowUpRight, ArrowDownLeft } from 'lucide-react';

import { API_URL as BACKEND_URL } from '../config/api';
import { formatName } from '../lib/format';
const fmt = (c) => `$${(Math.abs(c) / 100).toFixed(2)}`;

const statusConfig = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700 border-green-200' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700 border-red-200' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  pending_ach: { label: 'Bank transfer pending', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  pending_external: { label: 'Awaiting confirmation', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  pending: { label: 'Bank transfer pending', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  unknown: { label: 'Bank transfer pending', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  processing: { label: 'Bank transfer pending', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700 border-green-200' },
};

const CURRENT_SESSION = new Set(['active', 'pending_ach', 'pending_external']);
const CURRENT_PAYMENT = new Set(['pending', 'pending_ach', 'unknown']);

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const displayName = (name) => formatName(name);

const SplitsPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('current');
  const [hosted, setHosted] = useState([]);
  const [paid, setPaid] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [hRes, pRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/quick-split/my-sessions`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/quick-split/my-payments`, { credentials: 'include' }),
        ]);
        if (hRes.ok) setHosted(await hRes.json());
        if (pRes.ok) setPaid(await pRes.json());
      } catch (err) { console.error('Splits fetch error:', err); }
      setLoading(false);
    };
    fetchAll();
  }, []);

  // Normalize into unified rows with direction
  const hostedRows = hosted.map(qs => {
    const collectible = qs.collectible_count ?? Math.max(0, (qs.number_of_people || 1) - 1);
    const collectingCents = (qs.per_person_amount_cents || 0) * collectible;
    const remaining = Math.max(0, collectible - (qs.paid_count || 0));
    return {
      key: `h-${qs.id}`,
      direction: 'owed_to_me',
      title: qs.description || 'Quick Split',
      status: qs.status,
      isCurrent: CURRENT_SESSION.has(qs.status),
      // Display amount = what you're collecting (not full total)
      amount: collectingCents,
      totalCents: qs.total_amount_cents,
      collectingCents,
      progress: `${qs.paid_count} / ${collectible} collected`,
      detail: qs.status === 'active'
        ? (remaining === 0 ? 'All collected' : remaining === 1 ? 'Waiting on 1 person' : `Waiting on ${remaining} people`)
        : null,
      date: qs.created_at,
      onClick: () => navigate(`/quick-split/${qs.id}/host`),
    };
  });

  const paidRows = paid.map((p, i) => ({
    key: `p-${i}`,
    direction: 'i_owe',
    title: p.session_description || 'Quick Split',
    status: p.payment_status === 'completed' ? 'paid' : (p.payment_status || 'unknown'),
    isCurrent: CURRENT_PAYMENT.has(p.payment_status),
    amount: p.amount_cents,
    progress: null,
    detail: ['pending', 'pending_ach', 'unknown'].includes(p.payment_status) ? `Awaiting bank confirmation · To ${displayName(p.host_name)}` : `To ${displayName(p.host_name)}`,
    date: p.paid_at,
    onClick: null,
  }));

  const allRows = [...hostedRows, ...paidRows];
  const currentRows = allRows.filter(r => r.isCurrent).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const pastRows = allRows.filter(r => !r.isCurrent).sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const tabs = [
    { key: 'current', label: 'Current', count: currentRows.length },
    { key: 'past', label: 'Past', count: pastRows.length },
  ];

  const displayRows = tab === 'current' ? currentRows : pastRows;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="icon" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')} data-testid="splits-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-2">
              <Zap className="w-5 h-5 text-blue-600" />
              <h1 className="text-lg font-semibold text-gray-900">Quick Splits</h1>
            </div>
          </div>
          <Button size="sm" onClick={() => navigate('/quick-split/create')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="splits-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New Split
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5 sm:py-6">
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-4 sm:mb-6" data-testid="splits-tabs">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 py-2.5 px-3 rounded-md text-sm font-medium transition-colors ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`splits-tab-${t.key}`}>
              {t.label} {t.count > 0 && <span className="ml-1 text-xs text-gray-400">({t.count})</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
        ) : displayRows.length === 0 ? (
          <div className="text-center py-12">
            <Zap className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-1">
              {tab === 'current' ? 'No current Quick Splits' : 'No past Quick Splits'}
            </p>
            <p className="text-xs text-gray-400 mb-4">
              {tab === 'current' ? 'Active splits and pending payments will appear here' : 'Completed, expired, and closed splits will appear here'}
            </p>
            {tab === 'current' && (
              <Button onClick={() => navigate('/quick-split/create')} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4 mr-1" /> New Split
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid={`splits-${tab}-list`}>
            {displayRows.map(row => {
              const sc = statusConfig[row.status] || statusConfig.unknown;
              const isOwed = row.direction === 'owed_to_me';
              return (
                <div key={row.key}
                  className={`flex items-start p-4 bg-white border border-gray-200 rounded-xl transition-shadow ${row.onClick ? 'hover:shadow-sm cursor-pointer' : ''}`}
                  onClick={row.onClick || undefined}
                  data-testid={`split-row-${row.key}`}>

                  {/* Direction icon */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isOwed ? 'bg-green-50' : 'bg-blue-50'}`}>
                    {isOwed
                      ? <ArrowDownLeft className="w-4 h-4 text-green-600" />
                      : <ArrowUpRight className="w-4 h-4 text-blue-600" />
                    }
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 ml-3">
                    <div className="flex items-start justify-between mb-1">
                      <span className="text-sm font-semibold text-gray-900 truncate pr-2">{row.title}</span>
                      <span className="text-sm font-bold text-gray-900 flex-shrink-0">{fmt(row.amount)}</span>
                    </div>
                    <div className="flex items-center space-x-1.5 mb-1">
                      <span className={`text-xs font-medium ${isOwed ? 'text-green-600' : 'text-blue-600'}`}>
                        {isOwed ? 'Collecting' : 'You owe'}
                      </span>
                      <span className="text-gray-300">·</span>
                      <Badge className={`text-xs py-0 px-1.5 ${sc.color}`}>{sc.label}</Badge>
                    </div>
                    <div className="flex items-center text-xs text-gray-400">
                      {row.progress && <><span>{row.progress}</span><span className="mx-1.5">·</span></>}
                      {row.detail && <><span>{row.detail}</span><span className="mx-1.5">·</span></>}
                      {isOwed && row.totalCents != null && row.totalCents !== row.amount && (
                        <><span>{fmt(row.totalCents)} total</span><span className="mx-1.5">·</span></>
                      )}
                      <span>{fmtDate(row.date)}</span>
                    </div>
                  </div>

                  {row.onClick && <Eye className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1 ml-2" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SplitsPage;
